import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

export default class mbThreeJsScene {
  constructor(canvasId, modelPath, args = {}, modelLoadEvent = null) {
    /**
     * attributes & settings
     */
    this.canvas = document.getElementById(canvasId);
    this.canvasId = canvasId;
    this.scene = new THREE.Scene();
    this.mixer = null;
    this.clock = new THREE.Clock();
    this.camera = null;

    this.model = null;
    this.modelScene = null;
    this.modelPath = modelPath;
    this.modelLoadEvent = modelLoadEvent;

    this.loader = null;
    this.renderer = null;

    this.width = this.canvas ? this.canvas.offsetWidth : 0;
    this.height = this.canvas ? this.canvas.offsetHeight : 0;

    this.argsDefault = {
      cameraPerspective: {
        fov: 50,
        aspect: this.width / this.height, // default 1
        near: 0.1,
        far: 2000,
      },
      cameraPosition: {
        x: 0,
        y: 0,
        z: 0,
      },
      rotation: {
        x: 0,
        y: 0,
        z: 0,
      },
      scale: {
        x: 1,
        y: 1,
        z: 1,
      },
      // sizes: {
      //   width: window.innerWidth,
      //   height: window.innerHeight,
      // },
      animateInstantClips: [],
      animateOnScrollClips: [],
      scrollTrigger: {
        start: "top top", // when the top of the trigger hits the top of the viewport
        end: "bottom top", // when the bottom of the trigger hits the top of the viewport
      },
    };

    // merge custom args with default args
    this.args = { ...this.argsDefault, ...args };

    /**
     * Init scene, model, light and renderer
     */
    this.#setupScene();
    this.#addHDRImage();
    this.#loadModel();
    this.#setupSceneLight();
    this.#setupRenderer();

    /**
     * eventlistener
     */
    this.#onWindowResize();
  }

  /* ==================================================
   * Setter
   ================================================== */
  /**
   * Update rotation
   * @param {int} degree
   * @param {string} axis
   */
  setRotation(degree, axis) {
    const axisGrid = ["x", "y", "z"];
    if (!this.modelScene || !axisGrid.includes(axis)) return;
    this.modelScene.rotation[axis] = THREE.Math.degToRad(degree);
  }

  /* ==================================================
   * Getter
   ================================================== */
  getModel() {
    return this.modelScene;
  }

  /* ==================================================
   * Private methods
   ================================================== */
  /**
   * Initial scene setup
   * @returns
   */
  #setupScene() {
    // ========================== canvas
    if (!this.canvas) return;

    // ========================== camera
    if ("cameraPerspective" in this.args) {
      this.camera = new THREE.PerspectiveCamera(
        this.args.cameraPerspective.fov,
        this.args.cameraPerspective.aspect,
        this.args.cameraPerspective.near,
        this.args.cameraPerspective.far
      );
    }

    // ===== camera position
    if ("cameraPosition" in this.args) {
      this.camera.position.set(
        this.args.cameraPosition.x,
        this.args.cameraPosition.y,
        this.args.cameraPosition.z
      );
    }

    // ===== add camera to scene
    this.scene.add(this.camera);
  }

  /**
   * Load Model
   */
  #loadModel() {
    this.loader = new GLTFLoader();
    let $that = this;

    this.loader.load(
      this.modelPath,
      function (gltf) {
        $that.model = gltf;
        $that.modelScene = gltf.scene;

        // console.log("GLTF:", $that.model);
        // console.log("Model:", $that.modelScene);

        if ($that.modelLoadEvent) {
          $that.modelLoadEvent($that);
        }

        // ===== rotation
        if ("rotation" in $that.args) {
          $that.setRotation($that.args.rotation.x, "x");
          $that.setRotation($that.args.rotation.y, "y");
          $that.setRotation($that.args.rotation.z, "z");
        }

        // ===== scale
        if ("scale" in $that.args) {
          $that.modelScene.scale.set(
            $that.args.scale.x,
            $that.args.scale.y,
            $that.args.scale.z
          );
        }
        $that.scene.add($that.modelScene);

        // ===== mixer
        $that.mixer = new THREE.AnimationMixer($that.modelScene);

        // ===== run animations while scrolling
        for (let i = 0; i < $that.args.animateOnScrollClips.length; i++) {
          let clip = $that.model.animations[$that.args.animateOnScrollClips[i]];
          $that.#animateOnScrolling(clip);
        }

        // ===== run animations instant
        for (let i = 0; i < $that.args.animateInstantClips.length; i++) {
          // mixer.clipAction(clip).halt(2);
          let clip = $that.model.animations[$that.args.animateInstantClips[i]];
          $that.mixer.clipAction(clip).play();
        }
      },
      function (xhr) {
        // Upload status
        // console.log((xhr.loaded / xhr.total) * 100 + "% loaded");
      },
      function (error) {
        console.log("An error occurred", error);
      }
    );
  }

  /**
   * setup scene light
   */
  #setupSceneLight() {
    let light;
    light = new THREE.DirectionalLight(0xffffff, 3);
    light.position.set(1, 1, 1).normalize();
    this.scene.add(light);

    light = new THREE.DirectionalLight(0xffffff, 3);
    light.position.set(-1, -1, -1).normalize();
    this.scene.add(light);

    light = new THREE.DirectionalLight(0xffffff, 3);
    light.position.set(2, 2, 5);
    this.scene.add(light);

    // ambientLight = new THREE.AmbientLight(0xffffff);
    // scene.add(ambientLight);
  }

  /**
   * setup renderer
   */
  #setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      canvas: this.canvas,
      alpha: true,
    });
    this.renderer.setSize(this.width, this.height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    // this.renderer.outputEncoding = true;

    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1;
    this.renderer.outputEncoding = THREE.sRGBEncoding;

    this.#updateRatio(); // finaly update ratio

    let $that = this;
    animate();

    function animate() {
      if (!$that.scene || !$that.camera) return;
      requestAnimationFrame(animate);
      if ($that.clock) {
        let delta = $that.clock.getDelta();
        if ($that.mixer) $that.mixer.update(delta);
      }
      $that.renderer.render($that.scene, $that.camera);
    }
  }

  #onWindowResize() {
    window.addEventListener("resize", onWindowResize, false);
    let $that = this;

    function onWindowResize() {
      $that.#updateRatio();
    }
  }

  /* ==================================================
   * Private methods // Animation
   ================================================== */
  /**
   * update ratio
   *
   */
  #updateRatio() {
    this.width = this.canvas.offsetWidth;
    this.height = this.canvas.offsetHeight;
    this.camera.aspect = this.width / this.height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.width, this.height);
  }

  /**
   * init Scroll trigger
   * for animation by scrolling
   * @param {THREE clip} clip
   */
  #animateOnScrolling(clip) {
    gsap.registerPlugin(ScrollTrigger);
    let action = this.mixer.clipAction(clip);
    action.play();

    let $that = this;

    let proxy = {
      get time() {
        return $that.mixer.time;
      },
      set time(value) {
        action.paused = false;
        $that.mixer.setTime(value);
        action.paused = true;
      },
    };

    // ========================== ScrollTrigger
    // === Docs: https://greensock.com/docs/v3/Plugins/ScrollTrigger
    gsap.config({
      force3D: true,
    });

    let scrollingTL = gsap.timeline({
      scrollTrigger: {
        trigger: this.renderer.domElement, // starts when element enters viewport
        start: this.args.scrollTrigger.start, // when the top of the trigger hits the top of the viewport
        end: this.args.scrollTrigger.end, // end after scrolling 500px beyond the start
        pin: true,
        pinType: "fixed",
        scrub: true,
        // invalidateOnRefresh: true,
        onUpdate: function () {
          $that.camera.updateProjectionMatrix();
        },
      },
    });

    // ========================== Timeline
    // === Docs: https://greensock.com/docs/v3/GSAP/gsap.timeline()
    scrollingTL.to(proxy, {
      time: clip.duration,
      repeat: 0,
    });
  }

  /**
   * Add HDR IMAGE for lighting
   */
  #addHDRImage() {
    let $that = this;
    new RGBELoader()
      .setPath(globalvars_scene["stylesheet_directory_url"] + "/public/models/")
      .load("WindowStudio.hdr", function (texture) {
        texture.mapping = THREE.EquirectangularReflectionMapping;

        // $that.scene.background = texture;
        $that.scene.environment = texture;

        $that.renderer.render($that.scene, $that.camera);
      });
  }
}
